import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUS_CLS = { requested: "bg-amber-100 text-amber-800", verified: "bg-sky-100 text-sky-800", paid: "bg-emerald-100 text-emerald-800" };

// Earnings withdrawal requests: verify payout details → export CSV for the bank → mark paid.
export const WithdrawalQueue = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState("");
  const load = () => adminApi.get("/withdrawal-requests", { params: status ? { status } : {} }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (id, action) => {
    let body;
    if (action === "mark-paid") {
      const utr = window.prompt("Bank transfer reference (UTR) — optional");
      if (utr === null) return;
      body = { utr };
    }
    setBusy(id);
    try { await adminApi.post(`/withdrawal-requests/${id}/${action}`, body); toast.success(action === "verify" ? "Verified" : "Marked paid"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(null);
  };
  const exportCsv = async () => {
    const r = await adminApi.get("/withdrawal-requests/export", { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "carryit-withdrawals.csv"; a.click();
  };
  return (
    <div data-testid="withdrawal-queue">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">Each request is emailed to the operations inbox. Verify the payout details, transfer via the CSV, then mark paid.</p>
        <div className="flex items-center gap-2">
          <select data-testid="wd-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm">
            <option value="">Open</option><option value="requested">Requested</option><option value="verified">Verified</option><option value="paid">Paid</option>
          </select>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="withdrawals-export-btn">Export verified (CSV)</Button>
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-2 text-left">Requested</th><th className="px-4 py-2 text-left">Carrier</th><th className="px-4 py-2 text-left">Payout to</th><th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2"/></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((w) => (
              <tr key={w.id} data-testid={`wd-row-${w.id}`}>
                <td className="px-4 py-2 text-xs text-slate-600">{new Date(w.created_at).toLocaleString()}</td>
                <td className="px-4 py-2 text-xs">{w.carrier?.name}<div className="text-slate-400">{w.carrier?.phone}</div></td>
                <td className="px-4 py-2 text-xs font-mono">{w.account_number ? `${w.account_holder_name} · ${w.account_number} · ${w.ifsc_code}` : `UPI ${w.upi_id}`}</td>
                <td className="px-4 py-2 text-right font-medium tabular-nums">₹{Number(w.amount).toFixed(2)}</td>
                <td className="px-4 py-2"><Badge className={`text-[10px] uppercase ${STATUS_CLS[w.status] || ""}`} data-testid={`wd-status-${w.id}`}>{w.status}</Badge>{w.utr && <div className="text-[10px] font-mono text-slate-500 mt-0.5">UTR {w.utr}</div>}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {w.status === "requested" && <Button size="sm" disabled={busy === w.id} onClick={() => act(w.id, "verify")} data-testid={`wd-verify-${w.id}`}>Verify details</Button>}
                  {w.status === "verified" && <Button size="sm" disabled={busy === w.id} onClick={() => act(w.id, "mark-paid")} data-testid={`wd-paid-${w.id}`}>Mark paid</Button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-sm text-slate-400">No open withdrawal requests.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WithdrawalQueue;
